import type { Address } from 'viem';

import { sellTrackingCopy } from 'copy/transactions';

import {
  GenerateSignatureQueryResult,
  getMomentSignatureData,
} from 'hooks/web3/use-generate-moment-signature';
import { ChainId } from 'lib/chains';
import { UNSET_ARG } from 'lib/constants';
import {
  getCuratedStorePrepareEnabled,
  getTakeRateFromCuratedStore,
  getWorldIdFromCuratedStore,
} from 'utils/curated-store';
import { parseEther } from 'utils/units';

import { CuratedStore } from 'types/CuratedStore';
import { NftFilter } from 'types/Nft';

import { marketRouterClient } from './contract-clients';
import {
  DO_NOT_SIMULATE_CONTRACT_WRITE,
  SimulateContractWriteHelperOptions,
} from './simulate';

export type SellIntent = 'LIST' | 'EDIT' | 'UNLIST';

type ListValues = {
  auctionDuration: number | null;
  buyPrice: number | null;
  contractAddress: Address;
  reservePrice: number | null;
  /** number should be a unix timestamp in seconds. Null means "now". */
  saleStartsAt: number | null;
  tokenId: number;
  selectedCuratedStore: CuratedStore | null;
  signatureQuery: GenerateSignatureQueryResult;
  chainId: ChainId;
};

type UnlistValues = Pick<ListValues, 'contractAddress' | 'tokenId' | 'chainId'>;

type GetSellPrepareContractWriteConfigOptions = {
  configOptions: SimulateContractWriteHelperOptions;
} & (
  | { intent: Exclude<SellIntent, 'UNLIST'>; values: ListValues | null }
  | { intent: Extract<SellIntent, 'UNLIST'>; values: UnlistValues | null }
);

const intentActionNameMap = {
  LIST: 'list-nft',
  UNLIST: 'unlist-nft',
  EDIT: 'update-listing',
} as const;

/**
 * Returns related configurations that can be used to:
 *
 * - Prepare a contract write
 * - Show relevant transaction tracking copy
 */
export const getSellConfig = (
  options: GetSellPrepareContractWriteConfigOptions
) => {
  const { intent, values, configOptions } = options;

  if (!values) {
    return {
      actionName: null,
      prepareContractWriteConfig: DO_NOT_SIMULATE_CONTRACT_WRITE,
      trackingCopy: null,
    };
  }

  const actionName = intentActionNameMap[intent];

  if (intent === 'UNLIST') {
    return {
      actionName,
      prepareContractWriteConfig: getUnlistPrepareContractWriteConfig(
        {
          chainId: values.chainId,
          contractAddress: values.contractAddress,
          tokenId: values.tokenId,
        },
        configOptions
      ),
      trackingCopy: sellTrackingCopy.unlist(),
    };
  }

  const listContractWriteConfig = getListPrepareContractWriteConfig(
    values,
    configOptions
  );

  if (intent === 'EDIT') {
    return {
      actionName,
      prepareContractWriteConfig: listContractWriteConfig,
      trackingCopy: sellTrackingCopy.updateListing(),
    };
  }

  return {
    actionName,
    prepareContractWriteConfig: listContractWriteConfig,
    trackingCopy: sellTrackingCopy.list(),
  };
};

const getListPrepareContractWriteConfig = (
  values: ListValues,
  options: SimulateContractWriteHelperOptions = {}
) => {
  const {
    auctionDuration,
    buyPrice,
    contractAddress,
    reservePrice,
    tokenId,
    saleStartsAt,
    selectedCuratedStore,
    signatureQuery,
  } = values;

  const isSignatureReady = getCuratedStorePrepareEnabled({
    selectedCuratedStore,
    query: signatureQuery,
  });

  /**
   * if the signature is not ready, we don't want to prepare the contract write
   */
  if (!isSignatureReady) {
    return DO_NOT_SIMULATE_CONTRACT_WRITE;
  }

  return marketRouterClient.getSimulateConfig('upsertListing', {
    ...options,
    args: [
      // nftContract
      contractAddress,
      // tokenId
      BigInt(tokenId),
      // reservePrice
      reservePrice ? parseEther(reservePrice.toString()) : UNSET_ARG,
      // auctionDuration
      auctionDuration ? BigInt(auctionDuration) : UNSET_ARG,
      // shouldSetBuyPrice
      Boolean(buyPrice),
      // buyPrice
      buyPrice ? parseEther(buyPrice.toString()) : UNSET_ARG,
      // saleStartsAt
      saleStartsAt ? BigInt(saleStartsAt) : UNSET_ARG,
      // worldAssociation
      {
        // worldId
        worldId: BigInt(getWorldIdFromCuratedStore(selectedCuratedStore)),
        // takeRateInBasisPoints
        takeRateInBasisPoints:
          getTakeRateFromCuratedStore(selectedCuratedStore),
        // worldsApprovalData
        worldsApprovalData: getMomentSignatureData(signatureQuery),
      },
    ],
    chainId: values.chainId,
  });
};

export const getUnlistPrepareContractWriteConfig = (
  variables: NftFilter,
  options: SimulateContractWriteHelperOptions = {}
) => {
  const { chainId, contractAddress, tokenId } = variables;

  return marketRouterClient.getSimulateConfig('upsertListing', {
    ...options,
    args: [
      // nftContract
      contractAddress,
      // tokenId
      BigInt(tokenId),
      // reservePrice
      UNSET_ARG,
      // auctionDuration
      UNSET_ARG,
      // shouldSetBuyPrice
      false,
      // buyPrice
      UNSET_ARG,
      // saleStartsAt
      UNSET_ARG,
      // worldAssociation
      {
        // worldId
        worldId: UNSET_ARG,
        // takeRateInBasisPoints
        takeRateInBasisPoints: 0,
        // worldsApprovalData
        worldsApprovalData: '0x',
      },
    ],
    chainId,
  });
};
